import { EventType } from "@/analytics/analytics-events";
import { EditElementProperties } from "@/analytics/data-organization-events-properties";
import { updateProject } from "@/components/common/project-provider/update-project";
import { ElementIconType } from "@/components/ui/icons/element-icon-type";
import { createAssignTagsToIElementMutations } from "@/components/ui/tags/use-assign-tags-to-ielement";
import { createStaticProjectTagOptions } from "@/components/ui/tags/use-project-tag-options";
import { EditElementTagsBase } from "@/modes/sheet-mode/edit-waypoints/edit-waypoint-context-menu/edit-element-tags";
import { SphereXGColorPalette } from "@/modes/sheet-mode/edit-waypoints/edit-waypoint-context-menu/edit-waypoint-color";
import { selectTags } from "@/store/tags/tags-selectors";
import { Tag } from "@/store/tags/tags-slice";
import {
  selectAncestor,
  selectChildrenDepthFirst,
  selectIElement,
  selectRootIElement,
} from "@faro-lotv/app-component-toolbox";
import {
  DatePickerVariant,
  DateTimePicker,
  FaroText,
  FilterMenuOptionType,
  TextField,
} from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import { assert, GUID } from "@faro-lotv/foundation";
import {
  HoloBuilderColors,
  ILabel,
  isIElementGenericDataset,
  isIElementOverviewImage,
  isIElementRoomSection,
  isIElementSectionDataSession,
  isIElementTimeseries,
  isIElementTimeTravelSection,
  ISOTimeString,
  MAX_NAME_LENGTH,
} from "@faro-lotv/ielement-types";
import {
  createMutationSetElementCreatedAt,
  createMutationSetElementName,
  createMutationSetWaypointColor,
  Mutation,
} from "@faro-lotv/service-wires";
import { Stack } from "@mui/system";
import { useMemo, useState, type JSX } from "react";
import { ContextMenuAction, ContextMenuActionType } from "../action-types";

export const EDIT_ELEMENT_ACTION: ContextMenuAction = {
  type: ContextMenuActionType.editElement,
  label: "Edit",
  icon: ElementIconType.EditIcon,
  handler: async ({
    elementID,
    state,
    dark,
    apiClients,
    dispatch,
    createDialog,
    setConfirmButtonDisabled,
    confirmDialog,
    errorHandlers,
  }) => {
    const iElement = selectIElement(elementID)(state);
    if (!iElement) return;

    let newName: string | undefined;
    let newTimePoint: Date | undefined;
    let assignedTags:
      | { selected: Tag[]; options: FilterMenuOptionType[] }
      | undefined;
    // null => remove color, undefined => keep current color
    let newColor: HoloBuilderColors | null | undefined;

    const isTimeSeries = !!selectAncestor(
      iElement,
      isIElementTimeseries,
    )(state);

    const allowTimePointEditing =
      isIElementGenericDataset(iElement) ||
      isIElementSectionDataSession(iElement) ||
      isTimeSeries;

    const projectTags = selectTags(state);
    const tagOptions = createStaticProjectTagOptions(projectTags);

    // Allow tags editing on waypoints and time points (so both cases of Section/Room are allowed)
    const allowTagsEditing =
      isIElementRoomSection(iElement) || isIElementTimeTravelSection(iElement);
    const allowColorEditing = isIElementRoomSection(iElement) && !isTimeSeries;

    let isNameValid = true;
    let isTimePointValid = true;

    function updateConfirmButtonDisabled(): void {
      setConfirmButtonDisabled(!isNameValid || !isTimePointValid);
    }

    // Create edit confirmation dialog
    await createDialog({
      title: `Edit ${iElement.name}`,
      confirmText: "Save",
      isConfirmDisabled: true,
      dark,
      content: (
        <EditElementForm
          dark={dark}
          name={iElement.name}
          onNameChanged={(value, isValid) => {
            newName = value;
            isNameValid = isValid;

            updateConfirmButtonDisabled();
          }}
          allowTimePointEditing={allowTimePointEditing}
          initialTimePoint={iElement.createdAt}
          onTimePointChanged={(newDate) => {
            newTimePoint = newDate;
            isTimePointValid = !!newDate;

            updateConfirmButtonDisabled();
          }}
          allowTagsEditing={allowTagsEditing}
          projectTags={projectTags}
          tagOptions={tagOptions}
          initialTags={iElement.labels ?? undefined}
          onTagsChanged={(selected, options) => {
            assignedTags = { selected, options };

            updateConfirmButtonDisabled();
          }}
          allowColorEditing={allowColorEditing}
          initialColor={
            isIElementRoomSection(iElement)
              ? iElement.metaDataMap?.waypointColor
              : undefined
          }
          onColorChanged={(color) => {
            newColor = color;

            updateConfirmButtonDisabled();
          }}
          onEnterKey={() => confirmDialog()}
        />
      ),
      allowToCloseWithoutCancelling: true,
      size: isTimeSeries ? "m" : "s",
      showXButton: true,
      onConfirm: async () => {
        const mutations: Mutation[] = [];

        Analytics.track<EditElementProperties>(EventType.editElement, {
          via: "edit element dialog",
          elementType: iElement.type,
          // Need to nullish coalesce to undefined here because null is not allowed
          elementTypeHint: iElement.typeHint ?? undefined,
          hasEditedName: !!newName,
          hasEditedTimePoint: !!newTimePoint,
          hasEditedTags: !!assignedTags,
          hasEditedColor: newColor !== undefined,
        });

        const elementsToUpdate: GUID[] = [elementID];

        if (newName && isIElementOverviewImage(iElement)) {
          // overview maps can be duplicated in list of sheets of area (not only active area)
          // (one entry from capture tree, second is its copy in area).
          // They have different ID's but refer to the same image url and have same md5Hash value.
          // iElement is from capture tree and we need get the overview map copy located under
          // any area having same md5Hash value but different iElement.id .

          const root = selectRootIElement(state);
          assert(root, "Cannot find Root IElement of the project");

          const allOverviewMaps = selectChildrenDepthFirst(
            root,
            isIElementOverviewImage,
            5,
          )(state);

          const sheets = allOverviewMaps.filter(
            (obj) => obj.md5Hash === iElement.md5Hash && obj.id !== iElement.id,
          );

          sheets.forEach((sheet) => elementsToUpdate.push(sheet.id));
        }

        elementsToUpdate.forEach((id) => {
          if (newName) {
            mutations.push(createMutationSetElementName(id, newName));
          }
        });

        if (allowTimePointEditing && !!newTimePoint) {
          mutations.push(
            createMutationSetElementCreatedAt(
              elementID,
              newTimePoint.toISOString(),
            ),
          );
        }

        if (allowTagsEditing && assignedTags) {
          mutations.push(
            ...createAssignTagsToIElementMutations(
              iElement,
              assignedTags.selected,
              assignedTags.options,
            ),
          );
        }

        if (allowColorEditing && newColor !== undefined) {
          mutations.push(
            createMutationSetWaypointColor(elementID, newColor ?? undefined),
          );
        }

        try {
          const [resultMutation] =
            await apiClients.projectApiClient.applyMutations(mutations);
          if (resultMutation.status === "failure") {
            const errorMessage = resultMutation.message
              ? `Failed to edit element with error message: ${resultMutation.message}`
              : "Failed to edit element";
            throw new Error(errorMessage);
          }
          // Fetch the changed sub-tree and update the local copy of the project
          await dispatch(
            updateProject({
              projectApi: apiClients.projectApiClient,
              iElementQuery: {
                ancestorIds: elementsToUpdate,
              },
            }),
          );

          return true;
        } catch (error) {
          errorHandlers.handleErrorWithDialog({
            title: "Unable to edit Element",
            error,
          });
          return false;
        }
      },
    });
  },
};

type EditElementFormProps = {
  /** Label displayed on top of the field */
  name: string;

  /** Display the dark themed version of the dialog */
  dark?: boolean;

  /**
   * Callback executed when the name changed.
   *
   * @param name new value of the name entered by user
   * @param nameIsValid true if this new name is valid, false if invalid
   */
  onNameChanged(name: string, nameIsValid: boolean): void;

  /** Whether to allow editing of the time point */
  allowTimePointEditing: boolean;

  /** The initial time point in the form */
  initialTimePoint: ISOTimeString;

  /** Callback executed when the user changes the time point */
  onTimePointChanged?(newDate: Date | undefined): void;

  /** Whether to allow editing of the tags */
  allowTagsEditing: boolean;

  /** The initial tags in the form */
  initialTags?: ILabel[];

  /** All tags available in the project. */
  projectTags: Tag[];

  /** The allowed option elements */
  tagOptions: FilterMenuOptionType[];

  /** Callback executed when the user changes the tags */
  onTagsChanged(
    selectedTagOptions: Tag[],
    tagOptions: FilterMenuOptionType[],
  ): void;

  /** Whether to allow editing of the color */
  allowColorEditing: boolean;

  /** The initial color of the element */
  initialColor?: HoloBuilderColors;

  /** Callback when the color was changed */
  onColorChanged(newColor: HoloBuilderColors | null): void;

  /** Callback executed when the Enter key is pressed */
  onEnterKey(): void;
};

function EditElementForm({
  name,
  dark,
  onNameChanged,
  allowTimePointEditing,
  initialTimePoint,
  onTimePointChanged,
  allowTagsEditing,
  initialTags,
  projectTags,
  tagOptions,
  onTagsChanged,
  allowColorEditing,
  initialColor,
  onColorChanged,
  onEnterKey,
}: EditElementFormProps): JSX.Element {
  const [newName, setNewName] = useState(name);
  const [isFirstFocus, setIsFirstFocus] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [color, setColor] = useState(initialColor ?? null);
  const [selectedTags, setSelectedTags] = useState<Tag[]>(initialTags ?? []);

  const initialDate = useMemo(
    () => new Date(initialTimePoint),
    [initialTimePoint],
  );
  const [timePoint, setTimePoint] = useState<Date | undefined>(
    () => new Date(initialTimePoint),
  );

  return (
    <>
      <TextField
        dark={dark}
        label="Name"
        text={newName}
        autoFocus
        fullWidth
        onTextChanged={(value) => {
          setNewName(value);
          const errorMessage = validateElementName(value);
          onNameChanged(value, !errorMessage);
          setErrorMessage(errorMessage);
        }}
        inputProps={{ maxLength: MAX_NAME_LENGTH }}
        maxCharacterCount={MAX_NAME_LENGTH}
        shouldShowCounter
        error={errorMessage}
        onFocus={(event) => {
          // Select all text when the field is focused for the first time
          if (isFirstFocus) {
            event.target.select();
            setIsFirstFocus(false);
          }
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            onEnterKey();
          }
        }}
      />
      {allowTimePointEditing && (
        <DateTimePicker
          variant={dark ? DatePickerVariant.Dark : DatePickerVariant.Light}
          label="Time point"
          date={timePoint}
          initialDate={initialDate}
          onChange={(newDate) => {
            setTimePoint(newDate);
            onTimePointChanged?.(newDate);
          }}
          error={!timePoint}
        />
      )}

      {allowTagsEditing && (
        <Stack sx={{ gap: 0.5, my: 1 }}>
          <FaroText variant="labelM" dark={dark}>
            Tags
          </FaroText>
          <EditElementTagsBase
            tags={selectedTags}
            dark={dark}
            // We currently do not support nested dialogs, and using reactive store values are not supported in dialogs.
            // Disable the tags management, until we have clear feedback that we need to implement it.
            allowManageTags={false}
            projectTags={projectTags}
            tagOptions={tagOptions}
            onAssignTags={(newSelectedTags, selectableTags) => {
              setSelectedTags(newSelectedTags);
              onTagsChanged(newSelectedTags, selectableTags);
            }}
          />
        </Stack>
      )}

      {allowColorEditing && (
        <SphereXGColorPalette
          label="Color"
          hbColor={color}
          onHbColorChanged={(newColor) => {
            setColor(newColor);
            onColorChanged(newColor);
          }}
          sx={{ my: 1 }}
        />
      )}
    </>
  );
}

/**
 * Validates a new element name.
 *
 * @param name The name to validate
 * @returns An error message if the name is invalid, or undefined if the name is valid
 */
export function validateElementName(name: string): string | undefined {
  return name.trimStart().length === 0 ? "Name cannot be empty" : undefined;
}
